// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import OrderDetailPageTypeLink from "@guestsupply/Overrides/Components/OrderDetailPageTypeLink";
import { EMPTY_VALUE, GOOGLE_EVENTS, sharedDateTimeOptions } from "@guestsupply/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { FC } from "react";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    showPO?: boolean;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);

    return {
        language: getSession(state).language,
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentPage: getCurrentPage(state).type,
        currentBillTo: getCurrentBillToState(state).value,
        orderPageLink: getPageLinkByPageType(state, "OrderDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    loadOrderStatusMappings,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
}

// Xng-Changes: Check OrderSummaryCardExtensions file for style
export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    showPO,
    shouldLoadOrderStatusMappings,
    loadOrderStatusMappings,
    extendedStyles,
    // xng-change: add google event
    xGoogleAnalytics,
    currentPage,
    currentBillTo,
    orderPageLink,
}) => {
    const [styles] = useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    // XNG-Changes: change structure
    React.useEffect(() => {
        if (shouldLoadOrderStatusMappings) {
            loadOrderStatusMappings();
        }
    }, []);

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            ...sharedDateTimeOptions,
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const shipToNickName = order.properties?.shipToNickName ? order.properties?.shipToNickName : order.stCompanyName;
    const customerNumber = order.customerSequence ? order.customerSequence : order.customerNumber;

    // xng-change: add google event
    const getDataLayerEvent = (isOrderNumber?: boolean) => {
        const dataLayerEvent = {
            event:
                currentPage === "HomePage"
                    ? GOOGLE_EVENTS.MY_ACCOUNT_RECENT_ORDERS_HOME_PAGE
                    : GOOGLE_EVENTS.MY_ACCOUNT_RECENT_ORDERS,
            page_path: currentPage,
            click_text: isOrderNumber ? orderNumber : orderDateDisplay,
            destination: `${orderPageLink}?orderNumber=${orderNumber}`,
            brand_code: currentBillTo?.properties?.brand,
            exclusive: currentBillTo?.properties?.exclusive,
            group_descr: currentBillTo?.properties?.groupDescription,
        };

        if (currentPage === "HomePage") {
            return dataLayerEvent;
        }

        return {
            ...dataLayerEvent,
            affiliation: currentBillTo?.companyName,
        };
    };

    return (
        <>
            <DataTableCell {...styles.dataCell}>
                {orderDateDisplay && (
                    <OrderDetailPageTypeLink
                        title={orderDateDisplay}
                        orderNumber={orderNumber}
                        dataLayerEvent={
                            (xGoogleAnalytics.includeMyAccountRecentOrders ||
                                xGoogleAnalytics.includeMyAccountRecentOrdersHomePage) &&
                            getDataLayerEvent()
                        }
                    />
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {orderNumber && (
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={orderNumber}
                        dataLayerEvent={
                            (xGoogleAnalytics.includeMyAccountRecentOrders ||
                                xGoogleAnalytics.includeMyAccountRecentOrdersHomePage) &&
                            getDataLayerEvent(true)
                        }
                    />
                )}
            </DataTableCell>
            <DataTableCell {...styles.shipToCell}>
                {`${shipToNickName} #${customerNumber}` || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${order.statusDisplay || EMPTY_VALUE}`}</DataTableCell>
            {/* Xng-Changes: adding PO# */}
            {showPO && <DataTableCell {...styles.dataCell}>{`${order.customerPO || EMPTY_VALUE}`}</DataTableCell>}
            <DataTableCell {...styles.dataCell}>{`${order.orderGrandTotalDisplay || EMPTY_VALUE}`}</DataTableCell>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderSummaryCard);
